import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker.js";
import { useSelector } from "react-redux";
import moment from "moment";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import CurrencySelector from "../../components/CustomComponents/DropdownSelections/CurrencySelector.jsx";
import CoordinatePicker from "../../components/CustomComponents/Maps/CoordinatePicker.jsx";
import CompanyStaff from "./companyStaff.js";
import CompanyDevices from "./companyDevices.js";
import CompanySubscriptions from "./companySubscriptions.js";
import ManualBilling from "./manualBilling.js";
import CompanyBulkUpload from "./companyBulkUpload.js";
import {
  HOSPITALITY_COMPANY_TYPES,
  isHospitalityIndustry,
} from "../../Constants/companyTypes.js";

const mapCategoriesFromApi = (categories = []) => {
  const options = categories
    .filter((category) => (category?.isActive ?? category?.IsActive) === true)
    .map((category) => ({
      value: category.id ?? category.ID,
      label: category.name ?? category.Name,
    }));
  const selectedIds = categories
    .filter((category) => (category?.isSelected ?? category?.IsSelected) === true)
    .map((category) => category.id ?? category.ID);
  return { options, selectedIds };
};

const UpdateCompany = () => {
  const { CompID } = useParams();

  const navigate = useNavigate();
  const authCompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useSelector((state) => state.appSettings?.appDarkMode);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [companyData, setCompanyData] = useState({
    // From Company.cs
    CompID: parseInt(CompID),
    CompName: "",
    CompDetail: "",
    CompEmail: "",
    CompPhone: "",
    CompFax: "",
    CompIndustryType: "Hospitality",
    CompCurrency: "",
    CompCountry: "",
    CompCity: "",
    CompAddress: "",
    CompSecCode: "",
    CompType: "",
    CompRemarks: "",
    CompRegDate: moment().format("YYYY-MM-DD"), // Set to current date by default
    CompExpireDate: null,
    CompIcon: "/img/serveiq2.png",
    CompHeaderLogo: "",
    CompPNotificationLimit_PerDay: 10,
    CompTimeZone: "W. Europe Standard Time",
    CompUTCTimeZone: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    CompLatitude: "0",
    CompLongitude: "0",
    IsNeedtoSyncDB: false,
    DailyForecastBracket: false,
    IsArchieve: false,
    CompNumberOfDaysToShowExpiryWarning: 0,
    CompRecoveryEmail: "",
    CompRecoveryPhoneNumber: "",
    CompLanguage: "en",
    CompWebsite: "",
    CompFacebooklink: "",
    CompInstagramLink: "",
    CompYoutubeLink: "",
    CompTikTokLink: "",
    CompGoogleReview: "",
    CompPublish: false,
    IsOnlineMarketplaceEnabled: false,
    AllowPointOfSale: true,
    Marketplace_PlatformCommissionPercent: 0,
    Marketplace_PlatformCommissionFixedFee: 0,
    MarketplaceZone_ID: null,

    // From Company2.cs
    ParentComID: 0,
    LocationLicences: 0,
    IsCallerID: false,
    ISMobileApp: false,
    IsKitchenApp: false,
    ISWebApp: false,
    IsTVApp: false,
    IsWaiterApp: false,
    CompMaxIPadLicense: 1,
    WaiterAppMaxDevices: 0,
    CompMaxUserLicense: 1,
    // Set current date for hidden date fields as default to avoid DateTime.MinValue (1/1/0001) SQL errors
    MobileAppExpireDate: moment().format("YYYY-MM-DD"),
    WebAppExpireDate: moment().format("YYYY-MM-DD"),
    WaiterAppExpireDate: moment().format("YYYY-MM-DD"),
  });
  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);
  const [selectedIndustryType, setSelectedIndustryType] = useState(null);
  const [selectedCompType, setSelectedCompType] = useState(null);
  const [originalCompanySnapshot, setOriginalCompanySnapshot] = useState(null);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [selectedMarketplaceZone, setSelectedMarketplaceZone] = useState(null);
  const [restaurantCategoryOptions, setRestaurantCategoryOptions] = useState([]);
  const [selectedRestaurantCategoryIds, setSelectedRestaurantCategoryIds] = useState([]);
  const [maxRestaurantCategorySelection, setMaxRestaurantCategorySelection] = useState(3);

  const zoneSelectStyles = useMemo(
    () => (isDarkMode ? DropDownStylesDark : DropDownStyles),
    [isDarkMode]
  );

  const loadMarketplaceZones = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, {
        params: { IsActive: true, PageNumber: 1, PageSize: 200 },
      });
      if (response.data?.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setZoneOptions(
          rows.map((z) => ({
            value: Number(z.zoneId || z.Zone_ID),
            label: `${z.zoneName || z.ZoneName}${z.serviceCity || z.ServiceCity ? ` (${z.serviceCity || z.ServiceCity})` : ""}`,
          })).filter((z) => Number.isFinite(z.value) && z.value > 0)
        );
      } else {
        setZoneOptions([]);
      }
    } catch {
      setZoneOptions([]);
    }
  }, []);

  const loadRestaurantCategories = useCallback(async () => {
    const compId = parseInt(CompID, 10);
    if (!compId || !Number.isFinite(compId) || compId <= 0) return;
    try {
      const response = await axiosInstance.get(APIs.GET_SA_COMPANY_RESTAURANT_CATEGORIES, {
        params: { CompID: compId },
      });
      if (response?.data?.status) {
        const payload = response.data?.data || {};
        const categories = payload?.categories || payload?.Categories || [];
        const maxSelection = payload?.maxSelection || payload?.MaxSelection || 3;
        const { options, selectedIds } = mapCategoriesFromApi(categories);
        setMaxRestaurantCategorySelection(maxSelection);
        setRestaurantCategoryOptions(options);
        setSelectedRestaurantCategoryIds(selectedIds);
      } else {
        setRestaurantCategoryOptions([]);
        setSelectedRestaurantCategoryIds([]);
      }
    } catch {
      setRestaurantCategoryOptions([]);
      setSelectedRestaurantCategoryIds([]);
    }
  }, [CompID]);

  useEffect(() => {
    loadMarketplaceZones();
  }, [loadMarketplaceZones]);

  useEffect(() => {
    if (companyData.IsOnlineMarketplaceEnabled) {
      loadRestaurantCategories();
    }
  }, [companyData.IsOnlineMarketplaceEnabled, loadRestaurantCategories]);

  useEffect(() => {
    const rawZoneId =
      companyData.MarketplaceZone_ID ??
      companyData.marketplaceZone_ID ??
      companyData.marketplaceZoneId;
    const zoneId = rawZoneId != null && rawZoneId !== "" ? Number(rawZoneId) : null;
    if (!zoneId || !Number.isFinite(zoneId) || zoneId <= 0) {
      setSelectedMarketplaceZone(null);
      return;
    }
    const match = zoneOptions.find((o) => Number(o.value) === zoneId);
    setSelectedMarketplaceZone(
      match || { value: zoneId, label: `Zone ${zoneId}` }
    );
  }, [zoneOptions, companyData.MarketplaceZone_ID]);

  const mapApiCompanyToFormState = (data, compId) => ({
    CompID: parseInt(compId),
    LocalizeID: data.localizeID || data.LocalizeID || "en",
    CompName: data.compName || "",
    CompDetail: data.compDetail || "",
    CompEmail: data.compEmail || "",
    CompPhone: data.compPhone || "",
    CompFax: data.compFax || "",
    CompIndustryType: data.compIndustryType || "Hospitality",
    CompCurrency: data.compCurrency || data.CompCurrency || "",
    CompCountry: data.compCountry || "",
    CompCity: data.compCity || "",
    CompAddress: data.compAddress || "",
    CompSecCode: data.compSecCode || "",
    CompType: data.compType || "",
    CompRemarks: data.compRemarks || "",
    CompRegDate: data.compRegDate ? moment(data.compRegDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
    CompExpireDate: data.compExpireDate ? moment(data.compExpireDate).format("YYYY-MM-DD") : null,
    CompIcon: data.compIcon || "/img/serveiq2.png",
    CompHeaderLogo: data.compHeaderLogo || "",
    CompCoverImage: data.compCoverImage || "",
    CompPNotificationLimit_PerDay: data.compPNotificationLimit_PerDay ?? 10,
    CompTimeZone: data.compTimeZone || "W. Europe Standard Time",
    CompUTCTimeZone: data.compUTCTimeZone || "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    CompLatitude: data.compLatitude ?? data.CompLatitude ?? "0",
    CompLongitude: data.compLongitude ?? data.CompLongitude ?? "0",
    IsNeedtoSyncDB: data.isNeedtoSyncDB ?? false,
    DailyForecastBracket: data.dailyForecastBracket ?? false,
    IsArchieve: data.isArchieve ?? false,
    CompNumberOfDaysToShowExpiryWarning: data.compNumberOfDaysToShowExpiryWarning ?? 0,
    CompRecoveryEmail: data.compRecoveryEmail || "",
    CompRecoveryPhoneNumber: data.compRecoveryPhoneNumber || "",
    CompLanguage: data.compLanguage || "en",
    CompWebsite: data.compWebsite || "",
    CompFacebooklink: data.compFacebooklink || "",
    CompInstagramLink: data.compInstagramLink || "",
    CompYoutubeLink: data.compYoutubeLink || "",
    CompTikTokLink: data.compTikTokLink || "",
    CompGoogleReview: data.compGoogleReview || "",
    CompPublish: data.compPublish ?? false,
    IsOnlineMarketplaceEnabled: data.isOnlineMarketplaceEnabled ?? data.isWebApp ?? false,
    AllowPointOfSale: data.allowPointOfSale ?? data.AllowPointOfSale ?? true,
    Marketplace_PlatformCommissionPercent:
      data.marketplace_PlatformCommissionPercent ??
      data.Marketplace_PlatformCommissionPercent ??
      0,
    Marketplace_PlatformCommissionFixedFee:
      data.marketplace_PlatformCommissionFixedFee ??
      data.Marketplace_PlatformCommissionFixedFee ??
      0,
    MarketplaceZone_ID: (() => {
      const raw =
        data.marketplaceZone_ID ??
        data.MarketplaceZone_ID ??
        data.marketplaceZoneId ??
        null;
      const n = raw != null && raw !== "" ? Number(raw) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    CompanySlug: data.companySlug || data.CompanySlug || "",
    Com_Guid: data.com_Guid || data.Com_Guid || "",
    ParentComID: data.parentComID ?? 0,
    LocationLicences: data.locationLicences ?? 0,
    IsCallerID: data.isCallerID ?? false,
    ISMobileApp: data.isMobileApp ?? false,
    IsKitchenApp: data.isKitchenApp ?? false,
    ISWebApp: data.isOnlineMarketplaceEnabled ?? data.isWebApp ?? false,
    IsTVApp: data.isTVApp ?? false,
    IsWaiterApp: data.isWaiterApp ?? false,
    CompMaxIPadLicense: data.compMaxIPadLicense ?? 1,
    WaiterAppMaxDevices: data.waiterAppMaxDevices ?? 0,
    CompMaxUserLicense: data.compMaxUserLicense ?? 1,
    MobileAppExpireDate: data.mobileAppExpireDate ? moment(data.mobileAppExpireDate).format("YYYY-MM-DD") : null,
    WebAppExpireDate: data.webAppExpireDate ? moment(data.webAppExpireDate).format("YYYY-MM-DD") : null,
    WaiterAppExpireDate: data.waiterAppExpireDate ? moment(data.waiterAppExpireDate).format("YYYY-MM-DD") : null,
  });
  const fetchCompanyDetails = useCallback(async () => {

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.Get_COMPANY_BY_ID, {
        params: { CompID: CompID },
      });

      console.log(response.data);

      const data = response.data.data || response.data;
      const formattedData = mapApiCompanyToFormState(data, CompID);

      setOriginalCompanySnapshot(formattedData);
      setCompanyData(formattedData);
      setSelectedIndustryType(industryTypes.find((type) => type.value === data.compIndustryType));
      setSelectedCompType(
        HOSPITALITY_COMPANY_TYPES.find((type) => type.value === (data.compType || "")) || null
      );
      setSelectedCountry(data.compCountry || data.CompCountry || "");
      setSelectedCity(data.compCity || data.CompCity || "");
      const zoneId = Number(
        formattedData.MarketplaceZone_ID ||
          data.marketplaceZone_ID ||
          data.MarketplaceZone_ID ||
          data.marketplaceZoneId ||
          0
      );
      if (Number.isFinite(zoneId) && zoneId > 0) {
        setSelectedMarketplaceZone({ value: zoneId, label: `Zone ${zoneId}` });
      } else {
        setSelectedMarketplaceZone(null);
      }
    } catch (error) {
      showErrorToast("Failed to load company details");
      console.error("Error fetching company:", error);
    } finally {
      setLoading(false);
    }
  }, [CompID, authCompID, industryTypes]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCompanyData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "IsOnlineMarketplaceEnabled" ? { ISWebApp: value } : {}),
    }));
  }, []);

  const handleDateChange = useCallback((newValue, field) => {
    setCompanyData((prev) => ({
      ...prev,
      [field]: newValue ? moment(newValue).format("YYYY-MM-DD") : null,
    }));
  }, []);

  const handleCountryCityChange = useCallback((data) => {
    setCompanyData((prev) => ({
      ...prev,
      ...data,
    }));
    if (data.CompCountry !== undefined) {
      setSelectedCountry(data.CompCountry);
    }
    if (data.CompCity !== undefined) {
      setSelectedCity(data.CompCity);
    }
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    const { CompName, CompEmail, CompIndustryType, CompCurrency } = companyData;

    if (!CompName.trim()) {
      showErrorToast("Please provide a valid company name");
      return;
    }

    if (!CompEmail || !validateEmail(CompEmail)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (!CompIndustryType) {
      showErrorToast("Please select an industry type");
      return;
    }

    if (!CompCurrency) {
      showErrorToast("Please select a currency");
      return;
    }

    if (isHospitalityIndustry(CompIndustryType) && !companyData.CompType) {
      showErrorToast("Please select a company type for hospitality businesses.");
      return;
    }

    // Merge hidden DB fields from the original snapshot with current form values.
    let submitData = {
      ...(originalCompanySnapshot || {}),
      ...companyData,
      ISWebApp: companyData.IsOnlineMarketplaceEnabled,
      LocalizeID: originalCompanySnapshot?.LocalizeID || "en",
      CompID: parseInt(CompID),
      CompNumberOfDaysToShowExpiryWarning: parseInt(companyData.CompNumberOfDaysToShowExpiryWarning) || 0,
      CompPNotificationLimit_PerDay: parseInt(companyData.CompPNotificationLimit_PerDay) || 10,
      CompMaxIPadLicense: parseInt(companyData.CompMaxIPadLicense) || 1,
      CompMaxUserLicense: parseInt(companyData.CompMaxUserLicense) || 1,
      LocationLicences: parseInt(companyData.LocationLicences) || 0,
      WaiterAppMaxDevices: parseInt(companyData.WaiterAppMaxDevices) || 0,
      ParentComID: parseInt(companyData.ParentComID) || 0,
      AllowPointOfSale: companyData.AllowPointOfSale !== false,
      CompLatitude:
        companyData.CompLatitude === null || companyData.CompLatitude === undefined || companyData.CompLatitude === ""
          ? "0"
          : String(companyData.CompLatitude),
      CompLongitude:
        companyData.CompLongitude === null || companyData.CompLongitude === undefined || companyData.CompLongitude === ""
          ? "0"
          : String(companyData.CompLongitude),
      Marketplace_PlatformCommissionPercent: Math.min(
        100,
        Math.max(0, parseFloat(companyData.Marketplace_PlatformCommissionPercent) || 0)
      ),
      Marketplace_PlatformCommissionFixedFee: Math.max(
        0,
        parseFloat(companyData.Marketplace_PlatformCommissionFixedFee) || 0
      ),
      MarketplaceZone_ID:
        companyData.MarketplaceZone_ID > 0
          ? parseInt(companyData.MarketplaceZone_ID, 10)
          : null,
    };

    delete submitData.AndroidAppver;
    delete submitData.IOSAppver;

    // POS off: strip POS app flags only. Device license stays editable.
    if (!submitData.AllowPointOfSale) {
      submitData = {
        ...submitData,
        ISMobileApp: false,
        IsKitchenApp: false,
        IsWaiterApp: false,
        IsTVApp: false,
        IsCallerID: false,
      };
    }

    // Keep ISWebApp in sync with Online Ordering toggle.
    submitData.ISWebApp = !!submitData.IsOnlineMarketplaceEnabled;

    if (
      submitData.IsOnlineMarketplaceEnabled &&
      selectedRestaurantCategoryIds.length > maxRestaurantCategorySelection
    ) {
      showErrorToast(
        `You can select up to ${maxRestaurantCategorySelection} Go Fatafat categories only`
      );
      return;
    }

    // Helper function to validate date
    const isValidDate = (dateString) => {
      if (!dateString) return false;
      const date = moment(dateString, "YYYY-MM-DD", true);
      return date.isValid() && date.year() >= 1753 && date.year() <= 9999;
    };

    // CompRegDate: Always include with current date as fallback (with time component for SQL)
    const regDate = companyData.CompRegDate || moment().format("YYYY-MM-DD");
    if (isValidDate(regDate)) {
      submitData.CompRegDate = moment(regDate).format("YYYY-MM-DDTHH:mm:ss");
    } else {
      submitData.CompRegDate = moment().format("YYYY-MM-DDTHH:mm:ss");
      console.warn("Invalid CompRegDate, using current date:", submitData.CompRegDate);
    }
    
    // CompExpireDate: Optional, only include if provided
    if (companyData.CompExpireDate && isValidDate(companyData.CompExpireDate)) {
      submitData.CompExpireDate = moment(companyData.CompExpireDate).format("YYYY-MM-DDTHH:mm:ss");
    }
    
    // Hidden date fields: Keep existing data intact (whatever came from API)
    // Only format if valid, otherwise use current date to avoid DateTime.MinValue errors
    if (companyData.MobileAppExpireDate) {
      if (isValidDate(companyData.MobileAppExpireDate)) {
        submitData.MobileAppExpireDate = moment(companyData.MobileAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else if (originalCompanySnapshot?.MobileAppExpireDate && isValidDate(originalCompanySnapshot.MobileAppExpireDate)) {
        submitData.MobileAppExpireDate = moment(originalCompanySnapshot.MobileAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      }
    }
    
    if (companyData.WebAppExpireDate) {
      if (isValidDate(companyData.WebAppExpireDate)) {
        submitData.WebAppExpireDate = moment(companyData.WebAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else if (originalCompanySnapshot?.WebAppExpireDate && isValidDate(originalCompanySnapshot.WebAppExpireDate)) {
        submitData.WebAppExpireDate = moment(originalCompanySnapshot.WebAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      }
    }
    
    if (companyData.WaiterAppExpireDate) {
      if (isValidDate(companyData.WaiterAppExpireDate)) {
        submitData.WaiterAppExpireDate = moment(companyData.WaiterAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      } else if (originalCompanySnapshot?.WaiterAppExpireDate && isValidDate(originalCompanySnapshot.WaiterAppExpireDate)) {
        submitData.WaiterAppExpireDate = moment(originalCompanySnapshot.WaiterAppExpireDate).format("YYYY-MM-DDTHH:mm:ss");
      }
    }

    console.log("Final submitData:", JSON.stringify(submitData, null, 2));

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.POST_UPDATE_COMPANY, submitData);

      if (response.data.status) {
        if (submitData.IsOnlineMarketplaceEnabled) {
          try {
            const categoryResponse = await axiosInstance.put(
              APIs.PUT_SA_COMPANY_RESTAURANT_CATEGORIES,
              {
                CompID: parseInt(CompID, 10),
                categoryIds: selectedRestaurantCategoryIds,
              },
              { headers: { "Content-Type": "application/json" } }
            );
            if (!(categoryResponse?.data?.status === true)) {
              showErrorToast(
                categoryResponse?.data?.message ||
                  categoryResponse?.data?.Message ||
                  "Company saved, but Go Fatafat categories failed to update"
              );
              return;
            }
          } catch (categoryError) {
            showErrorToast(
              categoryError?.response?.data?.message ||
                categoryError?.response?.data?.Message ||
                "Company saved, but Go Fatafat categories failed to update"
            );
            return;
          }
        }
        showSuccessToast(response.data.message || "Company updated successfully");
        navigate("/company/manageCompany");
      } else {
        showErrorToast(response.data.message || "Failed to update company. Please check all fields.");
      }
    } catch (error) {
      console.error("Error updating company:", error);
      
      // Handle different error types
      if (error.response?.status === 404) {
        showErrorToast("Update company endpoint not found. Please check server configuration.");
      } else if (error.response?.status === 400) {
        // Validation error
        const errorData = error.response.data;
        if (errorData?.errors) {
          // Extract first error message
          const firstError = Object.values(errorData.errors)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          showErrorToast(errorMessage || "Validation error. Please check all fields.");
        } else if (errorData?.title) {
          showErrorToast(errorData.title);
        } else if (errorData?.message) {
          showErrorToast(errorData.message);
        } else {
          showErrorToast("Invalid data. Please check all required fields.");
        }
      } else if (error.response?.data?.message) {
        showErrorToast(error.response.data.message);
      } else if (error.response?.data?.title) {
        showErrorToast(error.response.data.title);
      } else if (error.message) {
        showErrorToast(`Error: ${error.message}`);
      } else {
        showErrorToast("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, [fetchCompanyDetails]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return renderGeneralTab();
      case "staff":
        return <CompanyStaff CompID={CompID} showAddForm={showAddStaffModal} setShowAddForm={setShowAddStaffModal} />;
      case "devices":
        return <CompanyDevices CompID={CompID} />;
      case "subscriptions":
        return (
          <CompanySubscriptions
            CompID={CompID}
            currency={companyData.CompCurrency || "USD"}
          />
        );
      case "invoices":
        return (
          <ManualBilling
            CompID={CompID}
            currency={companyData.CompCurrency || "USD"}
            parentComID={companyData.ParentComID || 0}
          />
        );
      case "bulkUpload":
        return <CompanyBulkUpload CompID={CompID} companyName={companyData.CompName} />;
      default:
        return renderGeneralTab();
    }
  };

  const renderGeneralTab = () => {
    return (
      <>
      <Panel>
        <PanelHeader>General</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Company Name"
                placeHolder="Enter Company Name"
                type="text"
                value={companyData.CompName}
                onchange={(e) => handleInputChange(e, "CompName")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Email"
                placeHolder="Enter Email Address"
                type="email"
                value={companyData.CompEmail}
                onchange={(e) => handleInputChange(e, "CompEmail")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Phone Number"
                placeHolder="Enter Phone Number"
                type="tel"
                value={companyData.CompPhone}
                onchange={(e) => handleInputChange(e, "CompPhone")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Fax Number"
                placeHolder="Enter Fax Number"
                type="tel"
                value={companyData.CompFax}
                onchange={(e) => handleInputChange(e, "CompFax")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <label>Industry Type</label>
              <Select
                value={selectedIndustryType}
                onChange={(option) => {
                  setSelectedIndustryType(option);
                  const nextIndustryType = option ? option.value : "";
                  const switchingAwayFromHospitality = !isHospitalityIndustry(nextIndustryType);
                  setSelectedCompType(switchingAwayFromHospitality ? null : selectedCompType);
                  setCompanyData({
                    ...companyData,
                    CompIndustryType: nextIndustryType,
                    ...(switchingAwayFromHospitality ? { CompType: "" } : {}),
                  });
                }}
                options={industryTypes}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Industry Type"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
            <div className="col-6">
              <label>Currency</label>
              <CurrencySelector
                value={companyData.CompCurrency}
                onChange={(option) => {
                  setCompanyData((prev) => ({
                    ...prev,
                    CompCurrency: option ? option.value : "",
                  }));
                }}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Currency"
              />
            </div>
          </div>

          {isHospitalityIndustry(companyData.CompIndustryType) && (
            <div className="row mb-3">
              <div className="col-6">
                <label>Company Type</label>
                <Select
                  value={selectedCompType}
                  onChange={(option) => {
                    setSelectedCompType(option);
                    setCompanyData({
                      ...companyData,
                      CompType: option ? option.value : "",
                    });
                  }}
                  options={HOSPITALITY_COMPANY_TYPES}
                  styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                  placeholder="Select Company Type"
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
          )}

          <div className="row mb-3">
            <CountryCitySelector
              selectedCountry={selectedCountry}
              selectedCity={selectedCity}
              setSelectedCity={setSelectedCity}
              onChange={handleCountryCityChange}
              setLoading={setLoading}
              selectedValueIsLabel={true}
              isDarkMode={isDarkMode}
            />
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Address"
                placeHolder="Enter Company Address"
                type="text"
                value={companyData.CompAddress}
                onchange={(e) => handleInputChange(e, "CompAddress")}
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Code"
                placeHolder="Enter Company Code"
                type="text"
                value={companyData.CompSecCode}
                onchange={(e) => handleInputChange(e, "CompSecCode")}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Latitude"
                placeHolder="Auto from map or type latitude"
                type="number"
                value={companyData.CompLatitude}
                onchange={(e) => handleInputChange(e, "CompLatitude")}
                step="0.000001"
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Longitude"
                placeHolder="Auto from map or type longitude"
                type="number"
                value={companyData.CompLongitude}
                onchange={(e) => handleInputChange(e, "CompLongitude")}
                step="0.000001"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <CoordinatePicker
                latitude={companyData.CompLatitude}
                longitude={companyData.CompLongitude}
                addressHint={
                  [companyData.CompAddress, companyData.CompCity, companyData.CompCountry]
                    .filter(Boolean)
                    .join(", ")
                }
                onSelect={(lat, lng) =>
                  setCompanyData((prev) => ({
                    ...prev,
                    CompLatitude: lat == null ? "0" : String(lat),
                    CompLongitude: lng == null ? "0" : String(lng),
                  }))
                }
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Push Notification Limit Per Day"
                placeHolder="Enter Push Notification Limit"
                type="number"
                value={companyData.CompPNotificationLimit_PerDay}
                onchange={(e) => handleInputChange(e, "CompPNotificationLimit_PerDay")}
                step="10"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <h6>Remarks</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Remarks"
                value={companyData.CompRemarks}
                onChange={(e) => handleInputChange(e, "CompRemarks")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
            <div className="col-6">
              <h6>Company Detail</h6>
              <textarea
                className="form-control"
                placeholder="Enter Company Detail"
                value={companyData.CompDetail}
                onChange={(e) => handleInputChange(e, "CompDetail")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
          </div>
        </PanelBody>
      </Panel>


      <Panel>
        <PanelHeader>Licenses & Limits</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-4">
              <FormInput
                label="Device License"
                placeHolder="Enter Device License"
                type="number"
                value={companyData.CompMaxIPadLicense}
                onchange={(e) => handleInputChange(e, "CompMaxIPadLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Max User License"
                placeHolder="Enter Max User License"
                type="number"
                value={companyData.CompMaxUserLicense}
                onchange={(e) => handleInputChange(e, "CompMaxUserLicense")}
                step="1"
              />
            </div>
            <div className="col-4">
              <FormInput
                label="Location Licenses"
                placeHolder="Enter Location Licenses"
                type="number"
                value={companyData.LocationLicences}
                onchange={(e) => handleInputChange(e, "LocationLicences")}
                step="1"
              />
            </div>
          </div>

        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>Dates & Settings</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration Start Date"
                value={companyData.CompRegDate}
                onChange={(newValue) => handleDateChange(newValue, "CompRegDate")}
                mode="date"
                placeholder="Select Date"
              />
            </div>
            <div className="col-6">
              <CustomDateTimePicker
                label="Registration End Date"
                value={companyData.CompExpireDate}
                onChange={(newValue) => handleDateChange(newValue, "CompExpireDate")}
                mode="date"
                placeholder="Select Date"
              />
            </div>
          </div>

          <div className="row mb-3 mt-3">
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.CompPublish}
                onToggle={(e) => handleInputChange(e, "CompPublish")}
                label="Publish Company"
                name="CompPublish"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.IsOnlineMarketplaceEnabled}
                onToggle={(e) => handleInputChange(e, "IsOnlineMarketplaceEnabled")}
                label="Go Fatafat"
                name="IsOnlineMarketplaceEnabled"
              />
            </div>
            <div className="col-4">
              <ToggleSwitch
                isChecked={companyData.AllowPointOfSale !== false}
                onToggle={(e) => handleInputChange(e, "AllowPointOfSale")}
                label="Point of Sale"
                name="AllowPointOfSale"
              />
            </div>
          </div>

          {companyData.IsOnlineMarketplaceEnabled && (
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Go Fatafat commission %</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  max="100"
                  step="0.01"
                  name="Marketplace_PlatformCommissionPercent"
                  value={companyData.Marketplace_PlatformCommissionPercent ?? 0}
                  onChange={(e) => handleInputChange(e, "Marketplace_PlatformCommissionPercent")}
                />
                <small className="text-muted">Percent of food subtotal (ex tip/delivery).</small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Go Fatafat fixed fee / order</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  step="0.01"
                  name="Marketplace_PlatformCommissionFixedFee"
                  value={companyData.Marketplace_PlatformCommissionFixedFee ?? 0}
                  onChange={(e) => handleInputChange(e, "Marketplace_PlatformCommissionFixedFee")}
                />
                <small className="text-muted">Optional flat fee added per Go Fatafat order.</small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Go Fatafat zone</label>
                <Select
                  options={zoneOptions}
                  value={selectedMarketplaceZone}
                  onChange={(opt) => {
                    const zoneId = opt?.value != null ? Number(opt.value) : null;
                    setSelectedMarketplaceZone(
                      opt
                        ? { value: zoneId, label: opt.label }
                        : null
                    );
                    setCompanyData((prev) => ({
                      ...prev,
                      MarketplaceZone_ID:
                        zoneId && Number.isFinite(zoneId) && zoneId > 0
                          ? zoneId
                          : null,
                    }));
                  }}
                  styles={zoneSelectStyles}
                  isClearable
                  placeholder="Select service zone"
                />
                <small className="text-muted">
                  Required for rider offers (must match rider active shift zone).
                </small>
              </div>
            </div>
          )}

          {companyData.IsOnlineMarketplaceEnabled && (
            <div className="row mb-3">
              <div className="col-12">
                <label className="form-label">
                  Go Fatafat categories (max {maxRestaurantCategorySelection})
                </label>
                <Select
                  options={restaurantCategoryOptions}
                  value={restaurantCategoryOptions.filter((option) =>
                    selectedRestaurantCategoryIds.includes(option.value)
                  )}
                  isMulti
                  closeMenuOnSelect={false}
                  onChange={(options) => {
                    const ids = (options || []).map((option) => option.value);
                    if (ids.length > maxRestaurantCategorySelection) {
                      showErrorToast(
                        `You can select up to ${maxRestaurantCategorySelection} categories only`
                      );
                      return;
                    }
                    setSelectedRestaurantCategoryIds(ids);
                  }}
                  styles={zoneSelectStyles}
                  placeholder="Select Go Fatafat categories"
                  isDisabled={loading}
                />
                <small className="text-muted">
                  Shown on Go Fatafat browse and search filters. Selected{" "}
                  {selectedRestaurantCategoryIds.length} of {maxRestaurantCategorySelection}.
                </small>
              </div>
            </div>
          )}
        </PanelBody>
      </Panel>
      </>
    );
  };

  return (
    <div>
      {loading && <Preloader />}
      
      {/* Tab Navigation */}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company/manageCompany">Manage Company</Link>
            </li>
            <li className="breadcrumb-item active">Update Company</li>
          </ol>
          <h1 className="page-header mb-0">Update Company</h1>
        </div>
        <div className="ms-auto">
          {activeTab === "general" && (
            <button
              onClick={handleSubmit}
              className="btn btn-success btn-rounded px-4 rounded-pill"
              disabled={loading}
            >
              <i className="fa fa-save fa-lg me-2 ms-n2 text-success-900"></i>
              {loading ? "Updating..." : "Update"}
            </button>
          )}
          {activeTab === "staff" && (
            <button
              onClick={() => setShowAddStaffModal(true)}
              className="btn btn-rounded px-4 rounded-pill"
              disabled={loading}
              style={{
                backgroundColor: '#00acac',
                background: 'linear-gradient(135deg, #00acac 0%, #00acac 100%)',
                border: 'none',
                color: 'white',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0, 172, 172, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #009999 0%, #009999 100%)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 172, 172, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #00acac 0%, #00acac 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 172, 172, 0.4)';
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
              Add Staff
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "general" ? "active" : ""}`}
                onClick={() => setActiveTab("general")}
                type="button"
                role="tab"
              >
                <i className="fa fa-building me-2"></i>
                General Information
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "staff" ? "active" : ""}`}
                onClick={() => setActiveTab("staff")}
                type="button"
                role="tab"
              >
                <i className="fa fa-users me-2"></i>
                Company Staff
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "devices" ? "active" : ""}`}
                onClick={() => setActiveTab("devices")}
                type="button"
                role="tab"
              >
                <i className="fa fa-mobile-alt me-2"></i>
                Company Devices
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "subscriptions" ? "active" : ""}`}
                onClick={() => setActiveTab("subscriptions")}
                type="button"
                role="tab"
              >
                <i className="fa fa-box me-2"></i>
                Subscriptions
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "invoices" ? "active" : ""}`}
                onClick={() => setActiveTab("invoices")}
                type="button"
                role="tab"
              >
                <i className="fa fa-file-invoice me-2"></i>
                Billing
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "bulkUpload" ? "active" : ""}`}
                onClick={() => setActiveTab("bulkUpload")}
                type="button"
                role="tab"
              >
                <i className="fa fa-upload me-2"></i>
                Bulk Upload
              </button>
            </li>
          </ul>
        </div>
        <div className="card-body">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default UpdateCompany;